import { useState } from 'react';
import type { UseDataTableState } from './use-data-table';
import { useDataTable } from './use-data-table';
import { useDynamicTableHeight } from './use-dynamic-table-height';
import { useLoadMoreSentinel } from './use-load-more-sentinel';
import { useTableToolbar } from './use-table-toolbar';
import type { UseTableToolbarState } from './use-table-toolbar';
import type { DataTableColumn, DataTableRow, ResolvedDataTableProps } from '@/shared/ui/data-table/types';

/** Long enough to swallow a burst of typing, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 250;

/** Everything the table body, chrome and footer render from. */
export type UseTableQueryState<TData extends DataTableRow> = {
  tableState: UseDataTableState<TData>;
  toolbar: UseTableToolbarState;
  /** Attach to the scrolling viewport so the hook can measure it. */
  viewportRef: (node: HTMLElement | null) => void;
  viewportStyle: { maxHeight: string } | undefined;
  sentinelRef: (node: HTMLElement | null) => void;
  /** Current column-bound filter values, keyed by column id. */
  columnFilters: Record<string, string>;
  /** Set a column-bound filter value. Pass an empty string to clear it. */
  setColumnFilter: (columnId: string, value: string) => void;
  /** Columns that declare a `filter` descriptor, in declaration order. */
  filterableColumns: DataTableColumn<TData>[];
};

/** Columns that contribute a filter control to the filter bar. */
function pickFilterableColumns<TData extends DataTableRow>(
  columns: DataTableColumn<TData>[],
): DataTableColumn<TData>[] {
  return columns.filter((column): column is DataTableColumn<TData> => column.filter !== undefined);
}

/**
 * Reads the initial values for a column-bound filter from the caller-supplied
 * `filters` prop. A saved view may pre-populate a filter by id, and we want
 * the control to reflect that on first render.
 */
function initialColumnFilters<TData extends DataTableRow>(
  columns: DataTableColumn<TData>[],
  filters: Record<string, string>,
): Record<string, string> {
  const initial: Record<string, string> = {};
  for (const column of pickFilterableColumns(columns)) {
    const value = filters[column.id];
    if (value !== undefined) initial[column.id] = value;
  }
  return initial;
}

/**
 * The whole server-query lifecycle of one table, in one place.
 *
 * Grouped rather than left in the component because these four are one concern
 * — what has been asked of the server, and what came back — and a component
 * that wires them inline is longer and harder to reason about than the markup
 * it is supposed to be reading.
 *
 * `pageSize` is the effective size (controlled prop, else the footer selector's
 * state); it lives in the component because the footer renders from it too.
 */
export function useTableQuery<TData extends DataTableRow>(
  props: ResolvedDataTableProps<TData>,
  pageSize: number,
): UseTableQueryState<TData> {
  const filterableColumns = pickFilterableColumns(props.columns);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() =>
    initialColumnFilters(props.columns, props.filters),
  );

  const tableState = useDataTable<TData>({
    columns: props.columns,
    data: props.data,
    total: props.total,
    rowKey: props.rowKey,
    selectable: props.selectable,
    defaultPage: props.defaultPage,
    defaultPageSize: props.defaultPageSize,
    pageSize,
    controlledPage: props.page,
    sort: props.sort,
    searchValue: props.searchValue ?? '',
    filters: props.filters,
    columnFilters,
    onFetch: props.onFetch,
    onSelectionChange: props.onSelectionChange,
    presentation: props.presentation,
  });

  const toolbar = useTableToolbar(tableState.table, {
    searchValue: props.searchValue,
    onSearchChange: props.onSearchChange,
    debounceMs: SEARCH_DEBOUNCE_MS,
  });

  const { viewportRef, viewportStyle } = useDynamicTableHeight({
    enabled: props.presentation === 'table',
  });

  // Armed when the caller opted out of paging, AND in card view: a grid has no
  // footer to page with, so scrolling is how the next page arrives.
  const { sentinelRef } = useLoadMoreSentinel({
    enabled: !props.paginated || props.presentation === 'cards',
    hasMore: tableState.hasNextPage,
    onLoadMore: tableState.nextPage,
  });

  const setColumnFilter = (columnId: string, value: string): void => {
    // The next value is computed OUTSIDE the state updater. React may run an updater
    // during render, and reporting upward from inside it would call the caller's
    // `setState` while a different component is rendering — which React rejects with
    // "Cannot update a component while rendering a different component".
    const next = { ...columnFilters };
    if (value === '') delete next[columnId];
    else next[columnId] = value;

    setColumnFilters(next);
    // Reported upward so a page whose read can be narrowed by the server can put
    // the filter in the request, rather than only in the rows already on screen.
    props.onColumnFiltersChange?.(next);
  };

  return {
    tableState,
    toolbar,
    viewportRef,
    viewportStyle,
    sentinelRef,
    columnFilters,
    setColumnFilter,
    filterableColumns,
  };
}
